import type { Decimal } from '../math/num';
import { D, mul, pow, floor } from '../math/num';
import type { Rarity } from './parts/partTypes';

/**
 * Salvage Drone Bay — the first Tech Find (MINIGAMES_AND_TECH_UNLOCKS #2), an
 * Expeditions/roguelite. You launch timed STRIKE SORTIES into the ruins; they
 * return Components (the interlock into MechMaxxing) and sometimes a Cache (a
 * guaranteed part, biased rarer than waves drop). Two dials, per the doc:
 *   • Clearance Level — the bay's level (Scrap sink); gates the max Escalation
 *     Tier and scales yield.
 *   • Escalation Tier — the per-run commitment: longer runs pay MORE total and
 *     slightly less per hour (set-and-forget friendly), at higher risk.
 * All timing is via elapsedMs and all randomness via the seeded RNG, so sorties
 * accrue deterministically — including offline.
 */

export const START_DRONES = 3;
export const MAX_DRONES = 8;

export interface Escalation {
  tier: number;
  name: string;
  durationMs: number;
  /** payout multiplier (encodes total yield; longer tiers pay less per hour). */
  units: number;
  /** per-drone chance to break on this sortie. */
  riskPct: number;
  /** chance to bring back a Cache (a guaranteed part). */
  cachePct: number;
}

const MIN = 60_000;
const HR = 3_600_000;

/** Strike Sortie tiers. Per-hour yield falls as tiers lengthen (see units/duration). */
export const ESCALATIONS: Escalation[] = [
  { tier: 1, name: 'Recon Sortie', durationMs: 2 * MIN, units: 2, riskPct: 0, cachePct: 0.1 },
  { tier: 2, name: 'Sweep Sortie', durationMs: 8 * MIN, units: 7, riskPct: 0.05, cachePct: 0.25 },
  { tier: 3, name: 'Raid Sortie', durationMs: 30 * MIN, units: 24, riskPct: 0.12, cachePct: 0.45 },
  { tier: 4, name: 'Deep Operation', durationMs: 2 * HR, units: 80, riskPct: 0.22, cachePct: 0.7 },
  { tier: 5, name: 'Black-Site Run', durationMs: 6 * HR, units: 220, riskPct: 0.35, cachePct: 1 },
];

export function getEscalation(tier: number): Escalation | undefined {
  return ESCALATIONS.find((e) => e.tier === tier);
}

/** Highest Escalation Tier the bay's Clearance permits (Clearance 1 → Tier 1). */
export function maxEscalationTier(clearance: number): number {
  return Math.max(1, Math.min(ESCALATIONS.length, clearance));
}

/** A bigger Mech sends meaner drones — keeps Component yield relevant per tier. */
function frameMult(frameTier: number): number {
  return Math.pow(2, Math.max(0, frameTier - 1));
}

/** Components a sortie yields (deterministic; no RNG). */
export function sortieComponents(clearance: number, escTier: number, drones: number, frameTier: number): Decimal {
  const esc = getEscalation(escTier);
  if (!esc) return D(0);
  const rate = 1.5 * Math.max(1, clearance) * Math.max(1, drones) * frameMult(frameTier);
  return floor(mul(D(rate), D(esc.units)));
}

/** Scrap to repair ALL broken drones (per broken drone, scales with Mech tier). */
export function repairCost(broken: number, frameTier: number): Decimal {
  return floor(mul(D(40 * broken), pow(D(2.4), frameTier - 1)));
}

/** Scrap to build the next drone (scales with how many you already have + tier). */
export function buildDroneCost(currentBots: number, frameTier: number): Decimal {
  return floor(mul(mul(D(120), pow(D(1.8), currentBots)), pow(D(2.2), frameTier - 1)));
}

/** Scrap to raise the bay's Clearance from `clearance` to `clearance+1`. */
export function bayUpgradeCost(clearance: number, frameTier: number): Decimal {
  return floor(mul(mul(D(250), pow(D(2.2), clearance - 1)), pow(D(2.4), frameTier - 1)));
}

/** Cache rarity weights — biased rarer than wave drops as Clearance/Tier climb. */
export function cacheRarityWeights(clearance: number, escTier: number): number[] {
  const t = Math.min(1, (clearance + escTier) / 10);
  // common, uncommon, rare, epic, legendary
  return [
    Math.max(0.1, 2 - t * 1.8),
    1.5 + t,
    0.8 + t * 2,
    0.3 + t * 2,
    0.05 + t * 1.2,
  ];
}

export const RARITY_LIST: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
